import React, { useEffect, useMemo, useState } from 'react';

import {
  getCategoryTree,
  getSkuCategorisation,
  updateSkuCategorisation,
} from '../../services/categoriesApi';
import PhocasHintPanel from './PhocasHintPanel';

const PRODUCT_TYPE_OPTIONS = ['residential', 'commercial', 'both'];

const flatten = (nodes, acc = []) => {
  for (const n of nodes) {
    acc.push(n);
    if (n.children?.length) flatten(n.children, acc);
  }
  return acc;
};

const CategoryAssignEditor = ({ sku, onSaved }) => {
  const [tree, setTree] = useState(null);
  const [current, setCurrent] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [draftCategories, setDraftCategories] = useState(null);
  const [draftProductType, setDraftProductType] = useState(null);
  const [search, setSearch] = useState('');
  const [savedJobId, setSavedJobId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [t, c] = await Promise.all([
          getCategoryTree(),
          getSkuCategorisation(sku),
        ]);
        if (cancelled) return;
        setTree(t);
        setCurrent(c);
        setDraftCategories(c.akeneo_categories || []);
        setDraftProductType(c.product_type || []);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || e.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sku]);

  const flatNodes = useMemo(() => (tree ? flatten(tree) : []), [tree]);
  const filtered = useMemo(() => {
    if (!search) return flatNodes;
    const q = search.toLowerCase();
    return flatNodes.filter(
      (n) => n.code.toLowerCase().includes(q) || (n.label || '').toLowerCase().includes(q),
    );
  }, [flatNodes, search]);

  const isDirty = useMemo(() => {
    if (!current) return false;
    const a = new Set(current.akeneo_categories || []);
    const b = new Set(draftCategories || []);
    const t1 = new Set(current.product_type || []);
    const t2 = new Set(draftProductType || []);
    if (a.size !== b.size || t1.size !== t2.size) return true;
    for (const v of a) if (!b.has(v)) return true;
    for (const v of t1) if (!t2.has(v)) return true;
    return false;
  }, [current, draftCategories, draftProductType]);

  const toggle = (code) => {
    setDraftCategories((arr) =>
      arr.includes(code) ? arr.filter((c) => c !== code) : [...arr, code],
    );
  };

  const toggleProductType = (code) => {
    setDraftProductType((arr) =>
      arr.includes(code) ? arr.filter((c) => c !== code) : [...arr, code],
    );
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setSavedJobId(null);
    try {
      const r = await updateSkuCategorisation(sku, {
        akeneo_categories: draftCategories,
        product_type: draftProductType,
      });
      setSavedJobId(r.job_id);
      setCurrent({
        ...current,
        akeneo_categories: r.akeneo_categories,
        product_type: r.product_type,
      });
      if (onSaved) onSaved(r);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (error) return <div className="error">Error: {error}</div>;
  if (!tree || !current) return <div>Loading editor…</div>;

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Categorise {sku}</h2>
      <p className="muted" style={{ marginTop: -8 }}>
        {current.product_name || current.name}
        {current.brand && <> · {current.brand}</>}
      </p>

      <PhocasHintPanel sku={sku} hints={current.phocas_hint} />

      <div className="card" style={{ marginTop: 12 }}>
        <h3>Product Type</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {PRODUCT_TYPE_OPTIONS.map((opt) => (
            <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input
                type="checkbox"
                checked={draftProductType.includes(opt)}
                onChange={() => toggleProductType(opt)}
              />
              {opt}
            </label>
          ))}
        </div>
        <p className="muted" style={{ marginTop: 6 }}>
          Akeneo attribute is multiselect; <code>both</code> is also a valid stand-alone value.
        </p>
      </div>

      <div className="card" style={{ marginTop: 12 }}>
        <h3>Categories</h3>
        <input
          type="search"
          placeholder="Filter by code or label…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: '100%', marginBottom: 8 }}
        />
        <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #2a3a52', padding: 4 }}>
          {filtered.map((n) => (
            <label
              key={n.code}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                paddingLeft: 8 + n.depth * 12,
                color: n.is_holding_area ? '#f0b' : 'inherit',
              }}
            >
              <input
                type="checkbox"
                checked={draftCategories.includes(n.code)}
                onChange={() => toggle(n.code)}
              />
              <span>{n.label}</span>
              <code style={{ color: '#7aa', fontSize: '0.85em' }}>{n.code}</code>
              {n.is_holding_area && (
                <span className="status" style={{ marginLeft: 6 }}>holding</span>
              )}
            </label>
          ))}
          {filtered.length === 0 && (
            <p className="muted" style={{ padding: 6 }}>No matches.</p>
          )}
        </div>
        <p className="muted" style={{ marginTop: 6 }}>
          Selected: {draftCategories.length} categor{draftCategories.length === 1 ? 'y' : 'ies'}.
        </p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
        <span className="muted">
          {isDirty ? 'Unsaved changes' : 'No changes'}
          {savedJobId && (
            <> · queued Akeneo job <code>#{savedJobId}</code></>
          )}
        </span>
        <button type="button" onClick={save} disabled={!isDirty || saving}>
          {saving ? 'Saving…' : 'Save & enqueue Akeneo push'}
        </button>
      </div>
    </div>
  );
};

export default CategoryAssignEditor;
